import axios from "axios";
import { resolveBaseURL } from "./apiClient";
import { clearStaleAuthStorage } from "./authStorage";

/**
 * Attach a 401 -> refresh-token -> retry interceptor to the given axios instance.
 *
 * Web clients use HttpOnly cookies, so the refresh token is sent automatically
 * by the browser; we only need to:
 *   1. Detect a 401 on a non-refresh request
 *   2. Call POST /refresh-token (single in-flight, shared across concurrent 401s)
 *   3. On success, retry the original request
 *   4. On failure, clear local auth state and bounce to /login
 */

let refreshPromise = null;

const isRefreshUrl = (url) => typeof url === "string" && url.includes("/refresh-token");

const performRefresh = () => {
  if (refreshPromise) return refreshPromise;
  refreshPromise = axios
    .post(`${resolveBaseURL()}/refresh-token`, {}, { withCredentials: true })
    .then((res) => res?.data?.success === true)
    .catch(() => false)
    .finally(() => {
      // Allow next 401 to start a fresh refresh attempt
      setTimeout(() => {
        refreshPromise = null;
      }, 0);
    });
  return refreshPromise;
};

const redirectToLogin = () => {
  if (typeof window === "undefined") return;
  clearStaleAuthStorage();
  const path = window.location.pathname || "";
  const isAdmin = path.startsWith("/admin");
  const target = isAdmin ? "/admin/login" : "/login";
  if (path !== target) {
    window.location.assign(target);
  }
};

export const attachAuthInterceptor = (instance) => {
  instance.interceptors.response.use(
    (res) => res,
    async (err) => {
      const original = err?.config;
      const status = err?.response?.status;

      if (
        status === 401 &&
        original &&
        !original._retry &&
        !isRefreshUrl(original.url)
      ) {
        original._retry = true;
        const ok = await performRefresh();
        if (ok) {
          return instance(original);
        }
        if (original.skipAuthRedirect) {
          return Promise.reject(err);
        }
        redirectToLogin();
      }

      return Promise.reject(err);
    }
  );
};
